import Evented from './Evented';
import metrics from './sharedMetrics';
import LongPollTransport from './transport/LongPollTransport';
import WebSocketTransport from './transport/WebSocketTransport';
const RETRY_DELAY = 5000;
function longPollParams(subscriptions) {
    // Firehose doesn't like true JSON, it wants numbers to be numbers
    const params = [];
    subscriptions.forEach((item) => {
        params.push(`"${item.channelPath}": ${item.lastMessageSequence || 0}`);
    });
    return `{${params.join(',')}}`;
}
function getParams(options) {
    return typeof options.params === 'function' ? options.params() : options.params || {};
}
function subscribeMessage(channel) {
    return {
        multiplex_subscribe: {
            channel: channel.channelPath,
            message_sequence: channel.lastMessageSequence,
            params: getParams(channel.options)
        }
    };
}
/**
 * Core 'channel' connection to firehose, without multiplexing
 * used by host channel as the common connection
 */
export default class HostChannel extends Evented {
    constructor(channelPath, options) {
        super();
        this.triedLongPoll = false;
        this.internalIsConnected = false;
        this.isReconnecting = false;
        this.subscribedTo = [];
        this.channelPath = channelPath;
        this.options = options;
        this.options.params = () => {
            if (this.transport instanceof WebSocketTransport) {
                return {};
            }
            else {
                return longPollParams(this.subscribedTo);
            }
        };
        this.transport = this.startTransport(WebSocketTransport);
        metrics.registerHost(this, this.options);
    }
    get isConnected() { return this.internalIsConnected; }
    get metrics() {
        return {
            connected: this.internalIsConnected,
            reconnecting: this.isReconnecting,
            lastConnected: this.lastConnected,
            lastHeartbeat: this.lastHeartbeat,
            transport: this.transport instanceof WebSocketTransport ? 'websocket' : 'longpoll',
            channels: this.subscribedTo.map((channel) => channel.metrics)
        };
    }
    subscribeToChannel(channel) {
        this.subscribedTo.push(channel);
        if (this.transport instanceof WebSocketTransport) {
            this.transport.sendMessage(subscribeMessage(channel));
        }
        else {
            this.transport.stop();
            this.transport.start();
        }
    }
    unsubscribeFromChannel(channel) {
        this.subscribedTo = this.subscribedTo.filter((item) => item !== channel);
        if (this.transport instanceof WebSocketTransport) {
            this.transport.sendMessage({
                multiplex_unsubscribe: [channel.channelPath]
            });
        }
        else {
            this.transport.stop();
            if (this.subscribedTo.length) {
                this.transport.start();
            }
        }
    }
    listenToTransport() {
        this.listenTo(this.transport, 'message', (msg, frame) => {
            this.heartbeat();
            if (msg && msg.error != null) {
                this.emit('error', msg.error, frame);
            }
            else {
                this.emit('message', msg, frame);
            }
        });
        this.listenTo(this.transport, 'failed', (reason, ...args) => {
            if (reason === 'connection') {
                if (!this.triedLongPoll) {
                    this.emit('switching', 'long-poll');
                    this.switchToLongPoll();
                }
                else {
                    this.resetAndRetry();
                }
            }
            else {
                this.emit('failed', reason, ...args);
            }
        });
        this.listenTo(this.transport, 'connected', () => this.emit('connected'));
        this.listenTo(this.transport, 'reconnecting', () => {
            this.isReconnecting = true;
            this.emit('reconnecting');
        });
        this.listenTo(this.transport, 'up', () => {
            if (this.isReconnecting) {
                this.resubscribeToChannels();
                this.isReconnecting = false;
            }
            this.internalIsConnected = true;
            this.lastConnected = new Date();
            this.lastHeartbeat = new Date();
            this.emit('up');
        });
        this.listenTo(this.transport, 'down', () => {
            this.internalIsConnected = false;
            this.emit('down');
        });
        this.listenTo(this.transport, 'heartbeat', () => this.heartbeat());
    }
    startTransport(Transport) {
        this.transport = new Transport(this.channelPath, this.options);
        this.listenToTransport();
        // We want the WebSocket open always, LongPoll needs something to subscribe to
        if (this.subscribedTo.length || this.transport instanceof WebSocketTransport) {
            this.transport.start();
        }
        return this.transport;
    }
    switchToLongPoll() {
        this.triedLongPoll = true;
        this.transport.stop();
        this.stopListening();
        this.startTransport(LongPollTransport);
    }
    resetAndRetry() {
        this.triedLongPoll = false;
        this.internalIsConnected = false;
        this.transport.stop();
        this.stopListening();
        this.emit('down');
        setTimeout(() => {
            this.isReconnecting = true;
            this.emit('reconnecting');
            this.startTransport(WebSocketTransport);
        }, RETRY_DELAY);
    }
    resubscribeToChannels() {
        if (this.transport instanceof WebSocketTransport) {
            this.subscribedTo.forEach((channel) => {
                this.transport.sendMessage(subscribeMessage(channel));
            });
        }
    }
    heartbeat() {
        this.lastHeartbeat = new Date();
        setTimeout(() => this.emit('heartbeat'), 0);
    }
}
//# sourceMappingURL=HostChannel.js.map